import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { micrositeService } from '@/services/microsite.service';
import { MicrositeAccessInfo } from '@/types/microsite';

const STORAGE_KEY = 'microsite_access';

export default function MicrositeAccessPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingStorage, setCheckingStorage] = useState(true);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [storedCustomerName, setStoredCustomerName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const checkStoredShare = () => {
      try {
        const storedData = localStorage.getItem(STORAGE_KEY);
        if (!storedData) {
          setCheckingStorage(false);
          return;
        }

        const parsed: MicrositeAccessInfo = JSON.parse(storedData);
        const { shareId, expiresAt, customerName } = parsed;

        // Check if the stored share has expired
        if (expiresAt && new Date(expiresAt) <= new Date()) {
          localStorage.removeItem(STORAGE_KEY);
          setCheckingStorage(false);
          return;
        }

        // Valid stored share — show welcome back screen
        if (shareId && customerName) {
          setStoredCustomerName(customerName);
          setShowWelcomeBack(true);
          setCheckingStorage(false);
          return;
        }
      } catch (err) {
        console.error('Error checking stored access:', err);
        localStorage.removeItem(STORAGE_KEY);
      }
      setCheckingStorage(false);
    };

    checkStoredShare();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await micrositeService.lookup(email);

      const accessInfo: MicrositeAccessInfo = {
        shareId: data.shareId,
        customerName: data.customerName,
        expiresAt: data.expiresAt,
        accessedAt: new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(accessInfo));

      navigate(`/p/${data.shareId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleWelcomeBackClick = () => {
    const storedData = localStorage.getItem(STORAGE_KEY);
    if (storedData) {
      const parsed: MicrositeAccessInfo = JSON.parse(storedData);
      navigate(`/p/${parsed.shareId}`);
    }
  };

  const handleNewUser = () => {
    localStorage.removeItem(STORAGE_KEY);
    setShowWelcomeBack(false);
    setStoredCustomerName('');
  };

  if (checkingStorage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-yellow-50 via-white to-yellow-100">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#bd9734] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Checking your access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <div className="relative min-h-screen flex flex-col">

        {/* Header with cropped image */}
        <div className="relative h-48 sm:h-64 overflow-hidden">
          <img
            src="/images/teebox-exterior.jpg"
            alt="Tee Box Golf"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          {/* Logo overlay */}
          <div className="absolute top-4 left-4">
            <img
              src="https://tbx.golf/oak/files/logos/logo.svg"
              alt="Tee Box"
              className="h-8 w-auto filter brightness-0 invert"
            />
          </div>
        </div>

        {/* Content area */}
        <div className="flex-1 px-6 py-8">
          <div className="max-w-md mx-auto">

            {showWelcomeBack ? (
              <div className="text-center space-y-6">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    Welcome back, {storedCustomerName.split(' ')[0]}!
                  </h1>
                  <p className="text-gray-600">
                    Ready to continue reviewing your golf development plan?
                  </p>
                </div>
                <div className="space-y-3">
                  <button
                    onClick={handleWelcomeBackClick}
                    className="w-full px-6 py-3 bg-[#bd9734] text-white rounded-xl font-semibold hover:bg-[#a68529] transition-all duration-200 shadow-lg"
                  >
                    View My Report
                  </button>
                  <button
                    onClick={handleNewUser}
                    className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:border-[#bd9734] hover:text-[#bd9734] transition-colors"
                  >
                    Access Different Account
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="text-center">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    Access Your Golf Report
                  </h1>
                  <p className="text-gray-600">
                    Enter your email to view your personalized assessment and development plan
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#bd9734] focus:border-transparent transition-all"
                      placeholder="your.email@example.com"
                      required
                      disabled={loading}
                    />
                  </div>

                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !email.trim()}
                    className="w-full px-6 py-3 bg-[#bd9734] text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#a68529] transition-all duration-200 shadow-lg"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Finding Your Report...
                      </span>
                    ) : (
                      'Access My Report'
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* Features highlight */}
            <div className="mt-12 grid grid-cols-3 gap-4 text-center">
              <div className="p-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <span className="text-green-600">📊</span>
                </div>
                <p className="text-xs text-gray-600">Detailed Analysis</p>
              </div>
              <div className="p-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <span className="text-blue-600">🎯</span>
                </div>
                <p className="text-xs text-gray-600">Custom Plan</p>
              </div>
              <div className="p-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <span className="text-purple-600">📈</span>
                </div>
                <p className="text-xs text-gray-600">Track Progress</p>
              </div>
            </div>

            <div className="mt-8 text-center text-sm text-gray-500">
              <p>
                Can't find your report?{' '}
                <span className="text-[#bd9734] font-medium">
                  Contact your golf professional
                </span>{' '}
                for assistance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
